import type { FC, ReactNode } from 'react';
import { AddIcon, MinusIcon } from '@chakra-ui/icons';
import { AccordionButton, AccordionItem, AccordionPanel, Link, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link as ReactRouterLink } from 'react-router-dom';
import { MessageDescriptorPropType } from './types';
import type { LinkGroupProps } from './types';

const getIcon = (isExpanded: boolean): React.ReactNode =>
    isExpanded ? <MinusIcon width={3.5} height={3.5} /> : <AddIcon width={3.5} height={3.5} />;

export const AccordionLinkGroup: FC<LinkGroupProps> = ({
    title,
    links,
    containerDataTestId,
    headerDataTestId,
}) => {
    const intl = useIntl();

    return (
        <AccordionItem
            border="none"
            borderTop="1px solid #CCC"
            data-testid={`${containerDataTestId}-accordion`}
        >
            {({ isExpanded }): ReactNode => (
                <>
                    <h2>
                        <AccordionButton>
                            <Text
                                color="slt-black.900"
                                fontFamily="Lato,sans-serif"
                                fontSize="0.75rem"
                                fontWeight="700"
                                lineHeight="normal"
                                letterSpacing="0.0625rem"
                                textTransform="uppercase"
                                data-testid={headerDataTestId}
                                flex="1"
                                textAlign="left"
                            >
                                <FormattedMessage {...title} />
                            </Text>
                            <div className="mr-4">{getIcon(isExpanded)}</div>
                        </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4}>
                        <ul className="mt-[1.63rem]">
                            {links.map((link) => (
                                <li key={link.id} className="mt-[1.56rem]">
                                    <Link
                                        id={link.componentId ?? undefined}
                                        title={intl.formatMessage(link.title)}
                                        data-testid={link.dataTestId}
                                        {...(link.isPwaLink
                                            ? { as: ReactRouterLink, to: link.url }
                                            : { href: link.url })}
                                        fontFamily="Lato,sans-serif"
                                        fontSize="0.75rem"
                                        fontWeight="400"
                                        letterSpacing="0.0625rem"
                                        textTransform="uppercase"
                                        target={link.showInNewTab ? '_blank' : undefined}
                                        onClick={
                                            link.componentId
                                                ? (e): void => e.preventDefault()
                                                : undefined
                                        }
                                        {...(!link.url ? { rel: 'nofollow' } : null)}
                                    >
                                        <FormattedMessage {...link.label} />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </AccordionPanel>
                </>
            )}
        </AccordionItem>
    );
};

AccordionLinkGroup.propTypes = {
    title: MessageDescriptorPropType.isRequired,
    links: PropTypes.arrayOf(
        PropTypes.exact({
            id: PropTypes.number.isRequired,
            url: PropTypes.string.isRequired,
            title: MessageDescriptorPropType.isRequired,
            label: MessageDescriptorPropType.isRequired,
            dataTestId: PropTypes.string,
            componentId: PropTypes.string,
            showInNewTab: PropTypes.bool,
            isPwaLink: PropTypes.bool.isRequired,
        }).isRequired,
    ).isRequired,
    containerDataTestId: PropTypes.string,
    headerDataTestId: PropTypes.string,
};

AccordionLinkGroup.defaultProps = {
    containerDataTestId: undefined,
    headerDataTestId: undefined,
};
